import { useEffect, useMemo, useRef, useState } from "react";
import { getRbacFleets, getRbacLocations } from "../Api/RbacApi";
import { getFleets, getOnboardingLocations } from "../Api/FleetOverviewApi";
import {
  isIsolatedFilterRoute,
  readGlobalFilterFromUrl,
  stripGlobalFilterFromUrl,
  transformOnboardingLocationsForHierarchy,
} from "../PagesContent/Overview/CommonFunctions";
import { useCanUseGlobalAdvancedFilter } from "../Helper/Permissions/GlobalAdvancedFilter";
import CODE from "../Static/Constants/StatusCodes";

const formatFleets = (fleets) => {
  if (!fleets || fleets.length === 0) return [];
  return fleets.map((fleet) => ({
    id: fleet.fleetID,
    name: fleet.fleetName,
    category: fleet.category,
  }));
};

const formatOnboardingLocations = (locations) => {
  if (!locations || locations.length === 0) {
    return { countries: [], states: [], cities: [], depots: [] };
  }
  const sets = {
    countries: new Set(),
    states: new Set(),
    cities: new Set(),
    depots: new Set(),
  };
  locations.forEach(({ originCountry: country, originState: state, originCity: city, originDepo: depot }) => {
    if (country) sets.countries.add(country);
    if (state) sets.states.add(state);
    if (city) sets.cities.add(city);
    if (depot) sets.depots.add(depot);
  });
  return {
    countries: [...sets.countries],
    states: [...sets.states],
    cities: [...sets.cities],
    depots: [...sets.depots],
  };
};

/**
 * Which customer accounts to start a page with.
 *
 * `storedIds` is the selection carried over from the page the user came from.
 * It is intersected with what they can see here rather than trusted, because a
 * permission change, an org switch or a stale id would otherwise filter the
 * grid down to accounts that no longer exist - an empty page with no visible
 * cause. An intersection that comes back empty falls back to everything for the
 * same reason: showing all is recoverable, showing nothing looks broken.
 *
 * A null storedIds means nothing has been chosen yet, which is different from
 * an empty selection, and also means everything.
 */
export const restoreFleetSelection = (allFleets = [], storedIds = null) => {
  if (!storedIds) return allFleets;
  const wanted = new Set(storedIds);
  const restored = allFleets.filter((fleet) => wanted.has(fleet.id));
  return restored.length ? restored : allFleets;
};

/**
 * Whether the filter bar's lists are still arriving.
 *
 * The two lists land separately, so a dimension that is empty is only
 * meaningfully empty once both have. The exception is a fleet list that came
 * back empty: every page gates its locations fetch on having at least one
 * accessible fleet, and a user with none has no vehicles and therefore no
 * locations, so nothing further is coming. Without that exception the filters
 * sit in their loading state forever for a user with no access - showing a
 * skeleton where they should show the no-access dash.
 */
export const isFilterDataLoading = ({ fleetsLoaded, locationsLoaded, fleetCount }) => {
  if (!fleetsLoaded) return true;
  if (fleetCount === 0) return false;
  return !locationsLoaded;
};

/**
 * Manages fleet and onboarding location data for pages that use either
 * GlobalFilter (RBAC APIs) or the legacy OnboardingLocationFilter (original APIs).
 * The choice is useCanUseGlobalAdvancedFilter: an RBAC v2 user with the global
 * advanced filter feature gets the RBAC APIs; v1 orgs always get the legacy ones.
 *
 * Returns:
 *   allFleets, selectedFleets, allOnboardingLocations (flat), allOnboardingLocationsHierarchy,
 *   globalFilters, setGlobalFilters,
 *   fetchFleets, fetchOnboardingLocations, handleFleetAndLocationChange,
 *   canUseGlobalAdvancedFilter
 *
 * Pages should call fetchFleets() in a useEffect on mount, and call
 * fetchOnboardingLocations(selectedFleets) in a useEffect when selectedFleets changes.
 *
 * For pages with a confirmation dialog (e.g. BmsFaults), wrap handleFleetAndLocationChange
 * in the page-level confirmation before calling it.
 */
const useGlobalFilterData = () => {
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();

  // The selection travels on links only. It is read from the URL of the page the
  // link opened and nowhere else: switching module from the nav rail, or
  // reloading, starts clean.
  //
  // Not applied on the custom dashboard, which is a separate platform with its
  // own filter config, nor without the permission - without it the page renders
  // the legacy location-only control, which has no customer-account dimension,
  // so a carried selection would scope the page with nothing on screen to show
  // for it.
  const carriedSelection = useMemo(() => {
    if (!canUseGlobalAdvancedFilter) return null;
    if (isIsolatedFilterRoute(window.location.pathname)) return null;
    return readGlobalFilterFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseGlobalAdvancedFilter]);

  // Consume it. Left in the address bar it would survive a reload, which is the
  // one thing a reload is meant to clear.
  useEffect(() => {
    if (carriedSelection) stripGlobalFilterFromUrl();
  }, [carriedSelection]);

  const [allFleets, setAllFleets] = useState([]);
  const [selectedFleets, setSelectedFleets] = useState([]);
  const [allOnboardingLocations, setAllOnboardingLocations] = useState({});
  const [allOnboardingLocationsHierarchy, setAllOnboardingLocationsHierarchy] = useState([]);
  const [globalFilters, setGlobalFilters] = useState({});
  const [fleetsLoaded, setFleetsLoaded] = useState(false);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  // The unscoped location tree cannot change with the customer selection, so
  // it is fetched once per mount and reused for every later call. The in-flight
  // promise is what gets held, so overlapping calls share one request.
  const locationsRequestRef = useRef(null);

  const fetchFleets = async () => {
    const response = canUseGlobalAdvancedFilter ? await getRbacFleets() : await getFleets();
    const formatted = formatFleets(response?.response?.data || []);
    setAllFleets(formatted);
    // Not `formatted`: defaulting to every fleet here is what discarded the
    // user's choice on every navigation.
    setSelectedFleets(restoreFleetSelection(formatted, carriedSelection?.fleetIds));
    setFleetsLoaded(true);
  };

  const fetchOnboardingLocations = async (fleets, extraFilters = {}) => {
    const fleetIds = fleets.map((f) => f.id);
    let response;
    if (canUseGlobalAdvancedFilter) {
      // Operating location is an independent filter: it is never scoped to the
      // customer selection, so the tree is always the full permitted one.
      if (!locationsRequestRef.current) {
        locationsRequestRef.current = getRbacLocations([]);
      }
      response = await locationsRequestRef.current;
      // GetData answers a failure with a well formed envelope rather than
      // throwing, so caching one would leave the filter empty for the life of
      // the mount. Drop it and let the next call retry.
      if (response?.responseStatus?.code !== CODE.SUCCESS) locationsRequestRef.current = null;
    } else {
      response = await getOnboardingLocations(fleetIds);
    }
    const rawLocations = response?.response?.data || [];
    const formatted = formatOnboardingLocations(rawLocations);
    const hierarchy = transformOnboardingLocationsForHierarchy(rawLocations);
    setAllOnboardingLocations(formatted);
    setAllOnboardingLocationsHierarchy(hierarchy);
    setLocationsLoaded(true);
    setGlobalFilters((prev) => ({
      ...prev,
      fleets: fleetIds,
      onboardingLocations: formatted,
      allOnboardingLocations: formatted,
      // Carried in from the previous page, but only against a tree that
      // actually arrived. Injecting it over an empty tree - a failed or empty
      // locations response - makes GlobalFilter read the value as stale and
      // clear it, which fires a second, differently scoped list fetch AND
      // erases the user's location filter on every page for the rest of the
      // session. A request failure must not do that.
      ...(hierarchy.length && carriedSelection?.hierarchicalLocation
        ? { hierarchicalLocation: carriedSelection.hierarchicalLocation }
        : {}),
      ...extraFilters,
    }));
  };

  const handleFleetAndLocationChange = ({ fleetIds, hierarchicalLocation }) => {
    const currentFleetIds = globalFilters.fleets || [];
    const fleetsChanged =
      JSON.stringify([...fleetIds].sort()) !== JSON.stringify([...currentFleetIds].sort());
    if (fleetsChanged) {
      setSelectedFleets(allFleets.filter((f) => fleetIds.includes(f.id)));
    }
    setGlobalFilters((prev) => ({ ...prev, fleets: fleetIds, hierarchicalLocation }));
  };

  return {
    allFleets,
    selectedFleets,
    allOnboardingLocations,
    allOnboardingLocationsHierarchy,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
    handleFleetAndLocationChange,
    canUseGlobalAdvancedFilter,
    fleetsLoaded,
    // GlobalFilter uses this to tell "still loading" apart from "the user can
    // reach nothing", which look identical from its props.
    filtersLoading: isFilterDataLoading({
      fleetsLoaded,
      locationsLoaded,
      fleetCount: allFleets.length,
    }),
  };
};

export default useGlobalFilterData;
